import { useEffect, useState } from "react";
import config from "./config";
import { cachedPost } from "./apiCache";

/**
 * Popup banner — a centered modal shown once per session per banner. The
 * banner's trigger decides WHEN it appears:
 *   onload       → a beat after the page paints
 *   delay        → after `delay` seconds
 *   exit_intent  → when the pointer leaves the top of the viewport
 */
function BannerPopup({ token, groupToken, placement = "community" }) {
  const [banner, setBanner] = useState(null);
  const [show, setShow] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    let active = true;
    let timer;
    let onExit;
    cachedPost(`${config.COMMUNITY_API_URL}/membership-banners`, {
        community_token: token,
        group_token: groupToken,
        placement,
      }, { ttl: 120000 })
      .then((res) => {
        if (!active || res.data?.error !== 0) return;
        // First popup on this page not yet dismissed this session.
        const popup = (res.data.popups || []).find(
          (p) => !sessionStorage.getItem(`banner_${token}_${p.id}`)
        );
        if (!popup) return;

        const reveal = () => {
          if (!active) return;
          setBanner(popup);
          // Next frame so the entrance transition runs from the hidden state.
          requestAnimationFrame(() => active && setShow(true));
        };

        const trigger = popup.trigger || "onload";
        if (trigger === "exit_intent") {
          onExit = (e) => {
            if (e.clientY <= 0) {
              document.removeEventListener("mouseout", onExit);
              reveal();
            }
          };
          document.addEventListener("mouseout", onExit);
        } else if (trigger === "delay") {
          timer = setTimeout(reveal, Math.max(0, popup.delay || 0) * 1000);
        } else {
          timer = setTimeout(reveal, 400); // onload — just after paint
        }
      })
      .catch(() => {});

    return () => {
      active = false;
      clearTimeout(timer);
      if (onExit) document.removeEventListener("mouseout", onExit);
    };
  }, [token, groupToken, placement]);

  const close = () => {
    if (banner) {
      try {
        sessionStorage.setItem(`banner_${token}_${banner.id}`, "1");
      } catch {
        /* ignore */
      }
    }
    setClosing(true);
    setTimeout(() => {
      setShow(false);
      setClosing(false);
    }, 200);
  };

  if (!show || !banner) return null;

  const image = (
    <img src={banner.image} alt="" className="block w-full" />
  );

  return (
    <div
      className={`fixed inset-0 z-[95] flex items-center justify-center bg-ink-deep/70 p-4 backdrop-blur-sm transition-opacity duration-200 ${
        closing ? "opacity-0" : "opacity-100"
      }`}
      onClick={close}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`relative w-full max-w-lg transition-all duration-300 ease-out ${
          closing ? "scale-95 opacity-0" : "scale-100 opacity-100"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          aria-label="Close"
          className="absolute -right-3 -top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-surface text-gray-600 shadow-lg ring-1 ring-black/5 transition hover:scale-105 hover:text-gray-900"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
        <div className="overflow-hidden rounded-2xl bg-surface shadow-2xl ring-1 ring-black/5">
          {banner.link ? (
            <a
              href={banner.link}
              target="_blank"
              rel="noreferrer"
              onClick={close}
              className="block"
            >
              {image}
            </a>
          ) : (
            image
          )}
        </div>
      </div>
    </div>
  );
}

export default BannerPopup;
